import { pdkAxios } from '@/lib/pdk-axios';
import type { AIDealSuggestion as AIDealSuggestionType } from '@/models/ai/types';
import { AIDealSuggestion, Deal } from '@/models/registry';
import { dealStore } from '@/models/stores/deals';
import type { CoreDeal } from '@/models/types';
import dayjs from 'dayjs';
import { makeAutoObservable } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { aiTaskSuggestions } from './task-suggestions';

type LLMDealResponseV3 = {
  facts: ExtractFacts;
  delta_packets: DeltaPacketChange[];
  suggestions: Suggestion[];
  comms: Comms;
};

type DealTaskSuggestion = {
  title: string;
  description: string;
  evidence: string[];
  why_blocking: string;
  priority: 'low' | 'medium' | 'high';
};

type Comms = {
  customer_sms_templates: string[];
  internal_checklist: string[];
  deal_tasks: DealTaskSuggestion[];
};

type Suggestion = {
  owner: 'sales' | 'finance' | 'ops';
  priority_level: 'low' | 'medium' | 'high';
  text: string;
  evidence: string[];
  due_by: string;
};

type DeltaPacketChange = {
  field: keyof CoreDeal;
  value: CoreDeal[keyof CoreDeal];
  evidence: string[];
  confidence: 'low' | 'medium' | 'high';
  why_blocking: string;
};

type ExtractFacts = {
  is_cash_buyer: string;
  wants_financing: string;
  preferred_visit_time: string;
  open_questions: string[];
  signals: string[];
  unanswered_customer_msgs: number;
};

class AIDealSuggestions {
  // <suggestion id, suggestion>
  store: Map<string, AIDealSuggestion> = new Map();

  // <deal_id_field, suggestion>
  storeIndexedByCompositeKey: Map<string, AIDealSuggestion[]> = new Map();

  // loading state for generating suggestions and delta packets
  suggestionsLoading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  get areSuggestionsLoading() {
    return this.suggestionsLoading;
  }

  getById(id: string): AIDealSuggestion | undefined {
    return this.store.get(id);
  }

  add(suggestion: AIDealSuggestionType) {
    const aiDealSuggestion = new AIDealSuggestion(suggestion);
    this.store.set(aiDealSuggestion.id, aiDealSuggestion);

    const compositeKey = `${aiDealSuggestion.deal_id}_${aiDealSuggestion.field}`;
    const arr = this.storeIndexedByCompositeKey.get(compositeKey) || [];
    arr.push(aiDealSuggestion);
    this.storeIndexedByCompositeKey.set(compositeKey, arr);
  }

  remove(
    id: AIDealSuggestionType['id'],
    dealId: AIDealSuggestionType['deal_id'],
    field: AIDealSuggestionType['field'],
  ) {
    this.store.delete(id);

    const compositeKey = `${dealId}_${field}`;
    const arr = this.storeIndexedByCompositeKey.get(compositeKey) || [];
    const index = arr.findIndex((s) => s.id === id);
    if (index === -1) return;

    arr.splice(index, 1);
    this.storeIndexedByCompositeKey.set(compositeKey, arr);
  }

  clear() {
    this.store.clear();
    this.storeIndexedByCompositeKey.clear();
  }

  // accepts a map of field names to the ID of the deal that changed
  // this way, we can remove suggestions that are no longer relevant for the deal and field
  removeFromMatchingDeltaPacket(diff: Map<string, string>) {
    for (const [field, dealId] of diff.entries()) {
      const suggestions = this.getSuggestionsByDealAndField(
        dealId,
        field as keyof CoreDeal,
      );
      for (const suggestion of suggestions) {
        this.saveAISuggestionDecision(
          suggestion.id,
          'dismissed_by_delta_packet',
        );
        this.remove(suggestion.id, dealId, field as keyof CoreDeal);
      }
    }
  }

  getSuggestionsByDealAndField(
    dealId: string,
    field: keyof CoreDeal,
  ): AIDealSuggestion[] {
    // return Array.from(this.store.values()).filter(
    //   (s) => s.deal_id === dealId && s.field === field,
    // );

    const compositeKey = `${dealId}_${field}`;
    return this.storeIndexedByCompositeKey.get(compositeKey) || [];
  }

  async generateSuggestionsForDealV2({ deal }: { deal: Deal }): Promise<void> {
    try {
      this.suggestionsLoading = true;
      const message = this.createDealAnalysisMessageV2({
        deal: deal,
      });

      const response = await pdkAxios.post<LLMDealResponseV3>(
        '/ml/generate-ai-suggestions-v2',
        {
          message,
        },
        {
          timeout: 30000,
        },
      );

      console.log('response', response.data);

      this.processLLMResponseV3(deal.id, response.data);
    } catch (error) {
      console.error('Error generating AI suggestions:', error);
    } finally {
      this.suggestionsLoading = false;
    }
  }

  createDealAnalysisMessageV2({ deal }: { deal: Deal }): string {
    return JSON.stringify(deal.toJSON());
  }

  createDealAnalysisMessage({
    deal,
    comment,
  }: {
    deal: CoreDeal;
    comment: string;
  }): string {
    return `        
        Current deal state:
        - Status: ${deal.status}
        - Stage: ${deal.stage}
        - Priority: ${deal.priority}
        
        Someone just added the following comment to the deal: ${comment}
        
        Based on this information, what status, stage, and priority would you recommend?
        If you do not have enough information to make a recommendation, do not add a suggestion for that field.
        Responses should be lowercase and snake_case.
    `;
  }

  processLLMResponseV3(dealId: string, response: LLMDealResponseV3): void {
    const nowTimestamp = dayjs().valueOf();

    // Process delta packets for deal field suggestions
    for (const {
      field,
      value,
      evidence,
      why_blocking,
    } of response.delta_packets) {
      const deal = dealStore.getById(dealId);
      if (!deal) return;

      if (value == 'not_applicable') {
        continue;
      }

      const suggestion: AIDealSuggestionType = {
        id: uuidv4(),
        created_at: nowTimestamp,
        deal_id: deal.id,
        field,
        current_value: deal[field],
        suggested_value: value,
        evidence,
        why_blocking,
      };

      // todo: handle some metrics here
      if (suggestion.current_value === suggestion.suggested_value) {
        continue;
      }

      this.add(suggestion);
    }

    // Process deal_tasks for task suggestions
    if (response.comms?.deal_tasks) {
      for (const dealTask of response.comms.deal_tasks) {
        const taskSuggestion =
          aiTaskSuggestions.createTaskSuggestionFromDealTask(dealId, dealTask);
        aiTaskSuggestions.add(taskSuggestion);
      }
    }
  }

  get all() {
    return Array.from(this.store.values());
  }

  saveAISuggestionDecision(
    id: AIDealSuggestionType['id'],
    decision: 'accepted' | 'dismissed' | 'dismissed_by_delta_packet',
  ) {
    const suggestion = this.getById(id);
    if (!suggestion) return;

    const currentValueString = this.stringifyValue(suggestion.current_value);
    const suggestedValueString = this.stringifyValue(
      suggestion.suggested_value,
    );

    pdkAxios.post('/ml/save-ai-suggestion', {
      id,
      entity_name: 'deal',
      entity_field: suggestion.field,
      record_id: suggestion.deal_id,
      accepted: decision === 'accepted',
      dismissed: decision === 'dismissed',
      dismissed_by_delta_packet: decision === 'dismissed_by_delta_packet',
      current_value: currentValueString,
      suggested_value: suggestedValueString,
    });
  }

  private stringifyValue(value: any): string {
    if (typeof value === 'string') {
      return value;
    }
    return value.toString();
  }
}

export const aiDealSuggestions = new AIDealSuggestions();
